import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Loader2, MapPin, User, CreditCard, ShoppingCart } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PromoCodeInput from '../components/PromoCodeInput';
import { useCartStore } from '../store/cartStore';
import { useShippingInfo } from '../hooks/useShippingInfo';
import { apiFetchJson } from '../lib/api';
import { formatPrice, normalizePhone, getReferralCode, getVisitorId, getVisitId } from '../lib/utils';
import { trackPageView, trackOrderCreated } from '../lib/analytics';
import type { PaymentMethod, DeliveryInfo } from '../types';

const DELIVERY_REGIONS = [
  { value: 'HK', label: '香港島' },
  { value: 'KLN', label: '九龍' },
  { value: 'NT', label: '新界' },
  { value: 'OS', label: '海外' },
];

// Used to auto-split a previously saved full phone number into country code + local number
const COUNTRY_CODE_PREFIXES = ['852', '86', '886', '65', '60', '1', '44', '61', '82', '81', '66', '852'];

export default function Checkout() {
  const navigate = useNavigate();
  const items = useCartStore((s) => s.items);
  const subtotal = useCartStore((s) => s.subtotal());
  const discount = useCartStore((s) => s.discount());
  const finalTotal = useCartStore((s) => s.finalTotal());
  const giftCode = useCartStore((s) => s.giftCode);
  const customerNote = useCartStore((s) => s.customerNote);
  const setCustomerNote = useCartStore((s) => s.setCustomerNote);
  const clearCart = useCartStore((s) => s.clearCart);
  const { label: shippingLabel } = useShippingInfo(finalTotal);

  const [delivery, setDelivery] = useState<DeliveryInfo>({
    name: '',
    phone: '',
    email: '',
    address: '',
    region: 'KLN',
  });
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('fps');
  const [kpayLive, setKpayLive] = useState(false);
  const [countryCode, setCountryCode] = useState('852');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // KPay 跳轉中：clearCart 後購物車已空——用全屏過渡頁頂住，防止渲染「購物車空的」誤導客人撳其他掣
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');

  // KPay 線上支付＝唯一付款方式（KPAY_LIVE=1）；開關關閉時回滾顯示 FPS/PayMe 人工流程
  useEffect(() => {
    apiFetchJson<{ kpay_live?: string }>('/public/settings')
      .then((res) => {
        if (res.success && res.data) {
          const live = res.data.kpay_live === '1';
          setKpayLive(live);
          // 線上支付開通→一律用 KPay（掃走 localStorage 殘留嘅 fps/payme）；未開通→KPay 殘留返 FPS
          setPaymentMethod((prev) => (live ? 'kpay' : prev === 'kpay' ? 'fps' : prev));
        }
      })
      .catch(() => {});
  }, []);

  // Load saved delivery info / payment method from localStorage
  useEffect(() => {
    trackPageView('/checkout');
    try {
      const saved = localStorage.getItem('x1493_delivery_info');
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<DeliveryInfo> & { paymentMethod?: PaymentMethod };
        setDelivery((prev) => ({
          ...prev,
          name: parsed.name || prev.name,
          phone: parsed.phone || prev.phone,
          email: parsed.email || prev.email,
          address: parsed.address || prev.address,
          region: parsed.region || prev.region,
        }));
        const savedPhone = parsed.phone || '';
        const matched = COUNTRY_CODE_PREFIXES.find((prefix) => savedPhone.startsWith(prefix));
        if (matched) {
          setCountryCode(matched);
          setPhoneNumber(savedPhone.slice(matched.length));
        } else {
          setCountryCode('852');
          setPhoneNumber(savedPhone);
        }
        if (parsed.paymentMethod) {
          setPaymentMethod(parsed.paymentMethod);
        }
      }
    } catch {
      // ignore corrupt localStorage
    }
  }, []);

  // Sync full phone number from country code + local number
  useEffect(() => {
    const digits = phoneNumber.replace(/\D/g, '');
    setDelivery((prev) => ({ ...prev, phone: countryCode + digits }));
  }, [countryCode, phoneNumber]);

  // Save delivery info / payment method to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        'x1493_delivery_info',
        JSON.stringify({ ...delivery, paymentMethod })
      );
    } catch {
      // ignore localStorage errors
    }
  }, [delivery, paymentMethod]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setDelivery((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return;

    setSubmitting(true);
    setError('');

    const phoneDigits = phoneNumber.replace(/\D/g, '');
    const fullPhone =
      countryCode === '852' && phoneDigits.startsWith('0')
        ? `852${phoneDigits.slice(1)}`
        : `${countryCode}${phoneDigits}`;
    const normalizedPhone = normalizePhone(fullPhone);

    try {
      const res = await apiFetchJson<{ orderNum: string; finalPrice: number; status: string }>('/public/orders', {
        method: 'POST',
        body: JSON.stringify({
          items: items.map((i) => ({
            productId: i.productId,
            name: i.name,
            quantity: i.quantity,
            unitPrice: i.unitPrice,
            subtotal: i.subtotal,
            // 套餐項附加（普通產品 undefined 欄位由 JSON.stringify 省略）
            ...(i.packageId ? { packageId: i.packageId, selections: i.selections || [] } : {}),
          })),
          totalPrice: subtotal,
          discountAmount: discount,
          finalPrice: finalTotal,
          giftCode: giftCode?.code || undefined,
          name: delivery.name,
          phone: normalizedPhone,
          email: delivery.email || undefined,
          address: delivery.address,
          region: delivery.region,
          remarks: customerNote,
          paymentMethod,
          referralCode: getReferralCode() || undefined,
          // 身份橋接：訂單帶 web dual-ID，落庫後每張訂單可回溯完整 web 旅程
          visitorId: getVisitorId(),
          visitId: getVisitId(),
        }),
      });

      if (res.success && res.data) {
        trackOrderCreated(res.data.orderNum, res.data.finalPrice);
        clearCart();
        if (res.data.status === 'payment_confirmed') {
          navigate(`/order/success?orderNum=${res.data.orderNum}`);
        } else if (paymentMethod === 'kpay') {
          // 線上支付：建 KPay 訂單 → 全頁跳轉收銀台；失敗時 fallback 手動付款流程（護欄：唔會冇路走）
          setRedirecting(true);
          try {
            const pay = await apiFetchJson<{ payOrigin: string; payPath: string }>(
              `/public/orders/${res.data.orderNum}/kpay/checkout`,
              { method: 'POST', body: JSON.stringify({}) }
            );
            if (pay.success && pay.data) {
              window.location.href = `${pay.data.payOrigin}${pay.data.payPath}`;
              return;
            }
            setError(pay.error || '線上付款暫時無法建立——訂單已保留，請喺訂單頁重試');
            setRedirecting(false);
            navigate(`/o/${res.data.orderNum}?total=${res.data.finalPrice}`);
          } catch {
            setRedirecting(false);
            navigate(`/o/${res.data.orderNum}?total=${res.data.finalPrice}`);
          }
        } else {
          // Kare：未接 WhatsApp 確認線——直接去訂單頁（FPS 指引＋付款憑證上傳，後台人手確認）
          navigate(`/o/${res.data.orderNum}?total=${res.data.finalPrice}`);
        }
      } else {
        setError(res.error || '建立訂單失敗，請重試');
      }
    } catch (err) {
      setError('網絡錯誤，請檢查連接後重試');
    } finally {
      setSubmitting(false);
    }
  };

  if (redirecting) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-200 border-t-x1493-primary rounded-full animate-spin mx-auto"></div>
          <h2 className="mt-6 text-xl font-bold text-stone-800">🔒 正在前往安全付款頁面…</h2>
          <p className="mt-2 text-sm text-stone-500">訂單已建立，請稍候，請勿關閉或按返回</p>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <section className="bg-x1493-dark text-white py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h1 className="text-3xl md:text-4xl font-bold">結帳</h1>
          </div>
        </section>
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="bg-stone-50 rounded-2xl p-12">
              <ShoppingCart size={64} className="mx-auto text-stone-300 mb-4" />
              <p className="text-lg text-stone-500 mb-6">購物車是空的，無法結帳</p>
              <Link to="/store" className="btn-primary inline-flex items-center gap-2">
                去商店逛逛 <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-x1493-dark text-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold">結帳</h1>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-stone-50 rounded-2xl p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <User className="text-x1493-secondary" size={22} />
                聯絡資料
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-1">姓名</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={delivery.name}
                    onChange={handleChange}
                    className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-1">電話號碼</label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      inputMode="numeric"
                      required
                      maxLength={4}
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="852"
                      className="w-20 p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none text-center"
                    />
                    <input
                      type="tel"
                      required
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="例如 9123 4567"
                      className="flex-1 p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none"
                    />
                  </div>
                  <p className="text-xs text-stone-500 mt-1">預設香港 +852，可直接修改區號</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-stone-700 mb-1">電郵（可選）</label>
                  <input
                    type="email"
                    name="email"
                    value={delivery.email}
                    onChange={handleChange}
                    className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <MapPin className="text-x1493-secondary" size={22} />
                送貨地址
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-1">地區</label>
                  <select
                    name="region"
                    required
                    value={delivery.region}
                    onChange={handleChange}
                    className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none bg-white"
                  >
                    {DELIVERY_REGIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-1">詳細地址</label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={delivery.address}
                    onChange={handleChange}
                    placeholder="大廈名稱、樓層、單位"
                    className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-1">客戶備註（可選）</label>
                  <textarea
                    name="customerNote"
                    rows={3}
                    value={customerNote}
                    onChange={(e) => setCustomerNote(e.target.value)}
                    placeholder="例如：唔夠辣唔收貨😂"
                    className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <CreditCard className="text-x1493-secondary" size={22} />
                付款方式
              </h2>
              <div className={`grid gap-4 ${kpayLive ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {!kpayLive && (
                <>
                <label
                  className={`cursor-pointer border-2 rounded-xl p-4 text-center font-bold transition ${
                    paymentMethod === 'fps'
                      ? 'border-x1493-primary bg-white text-x1493-primary'
                      : 'border-stone-200 text-stone-600 hover:border-stone-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="fps"
                    checked={paymentMethod === 'fps'}
                    onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                    className="sr-only"
                  />
                  轉數快 FPS
                </label>
                <label
                  className={`cursor-pointer border-2 rounded-xl p-4 text-center font-bold transition ${
                    paymentMethod === 'payme'
                      ? 'border-x1493-primary bg-white text-x1493-primary'
                      : 'border-stone-200 text-stone-600 hover:border-stone-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="payme"
                    checked={paymentMethod === 'payme'}
                    onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                    className="sr-only"
                  />
                  PayMe
                </label>
                </>
                )}
                {kpayLive && (
                <label
                  className={`cursor-pointer border-2 rounded-xl p-4 text-center font-bold transition ${
                    paymentMethod === 'kpay'
                      ? 'border-x1493-primary bg-white text-x1493-primary'
                      : 'border-stone-200 text-stone-600 hover:border-stone-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="kpay"
                    checked={paymentMethod === 'kpay'}
                    onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                    className="sr-only"
                  />
                  線上付款
                  <span className="block text-[10px] font-normal text-stone-400 mt-0.5">信用卡 · Apple Pay · AlipayHK · 微信（自動確認）</span>
                </label>
                )}
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl p-6">
              <h2 className="text-xl font-bold mb-4">訂單摘要</h2>
              <div className="space-y-2 mb-4">
                {items.map((item) => (
                  <div key={item.productId} className="flex justify-between text-sm">
                    <span className="text-stone-600">{item.name} × {item.quantity}</span>
                    <span className="font-medium">{formatPrice(item.subtotal)}</span>
                  </div>
                ))}
              </div>
              <PromoCodeInput orderAmount={subtotal} />
              <p className="text-xs text-stone-500 mt-2">
                如有推薦餘額（≥ HK$20），系統將自動用嚟扣減訂單金額。
              </p>
              <div className="border-t border-stone-200 mt-4 pt-4 space-y-2">
                <div className="flex justify-between text-stone-600">
                  <span>小計</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>優惠</span>
                    <span>-{formatPrice(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-stone-600">
                  <span>運費</span>
                  <span>{shippingLabel}</span>
                </div>
                <div className="flex justify-between text-2xl font-bold">
                  <span>合計</span>
                  <span className="text-x1493-primary">{formatPrice(finalTotal)}</span>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-xl font-medium">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-x1493-primary text-white text-lg font-bold py-4 rounded-2xl hover:bg-red-700 disabled:opacity-60 transition flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><Loader2 size={22} className="animate-spin" /> 處理中...</>
              ) : (
                <>確認訂單<ArrowRight size={22} /></>
              )}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
